// src/services/api.ts
import axios, { AxiosRequestConfig } from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  withCredentials: true,
});

/* ============================
   REQUEST INTERCEPTOR
============================ */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token") ?? sessionStorage.getItem("access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* ============================
   SINGLE-FLIGHT REFRESH STATE
============================ */
let isRefreshing = false;
// Requests that arrived while a refresh was in progress wait here.
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const flushQueue = (error: unknown, token: string | null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  pendingQueue = [];
};

// Bare axios call (NOT the `api` instance) so this request
// does not itself go through the interceptor and recurse.
const refreshAccessToken = async (): Promise<string> => {
  const res = await axios.post(
    `${api.defaults.baseURL}/auth/refresh`,
    {},
    { withCredentials: true } // sends the httpOnly refresh cookie
  );
  const newToken = res.data.token;
  if (!newToken) throw new Error("No token in refresh response");
  if (localStorage.getItem("access_token")) {
    localStorage.setItem("access_token", newToken);
  } else if (sessionStorage.getItem("access_token")) {
    sessionStorage.setItem("access_token", newToken);
  } else {
    localStorage.setItem("access_token", newToken);
  }
  return newToken;
};

const forceLogout = (error: unknown) => {
  localStorage.removeItem("access_token");
  sessionStorage.removeItem("access_token");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
  return Promise.reject(error);
};

/* ============================
   RESPONSE INTERCEPTOR
============================ */
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    if (!error.response) {
      console.error("API unreachable:", error);
      return Promise.reject({
        message: "Network error. Please check your connection.",
        status: 0,
      });
    }

    const { status } = error.response;
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (status === 401 && !originalRequest._retry) {
      // Never try to refresh the refresh call itself.
      if (originalRequest.url?.includes("/auth/refresh")) {
        return forceLogout(error);
      }

      // A refresh is already running — queue this request behind it.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (token: string) => {
              originalRequest._retry = true;
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        flushQueue(null, newToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (refreshErr) {
        flushQueue(refreshErr, null);
        return forceLogout(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 403) console.warn("Access forbidden");
    if (status >= 500) console.error("Server error:", error.response);

    return Promise.reject(error);
  }
);

export default api;